import { KalturaGroupingOption, KalturaRecordingStatus } from '@kaltura-ott/ott-client-types/api/types';
import { assetToType, UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { action, computed } from 'mobx';
import { container } from 'tsyringe';

import { featureEnabler } from '../../../../../utils';
import { errorHandling } from '../../../../../utils/error-handling';
import { CardPlaceholder, Program } from '../../../../entities';
import { creatorEntityStore } from '../../../../entities/creator.entity';
import { addBookmarkToRecords, addMosaicProgramsToPrograms } from '../../../../entities/entities.service';
import { ErrorPriority } from '../../../../error';
import { RecordRepository } from '../../../../recordRepository';
import { getItemsInPage, DEFAULT_PAGE_SIZE } from '../rail-store-helper-v2';
import { RailState, RailStoreV2 } from '../rail-store-v2';

export class RecordsStoreV2 extends RailStoreV2<Program> {
  subtype: UIDAPresentationTypeEnum.npvr = UIDAPresentationTypeEnum.npvr;
  groupBy = 'SeriesID';
  protected _removeInvisible = false;

  @action
  @errorHandling({
    type: ErrorPriority.low,
    callback: function (this: RecordsStoreV2, error) {
      this.errorHandlerCallback(error);
    },
  })
  async init() {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');
    recordRepository.saveCurrentRecordRailId(this.id);

    if (this.isInitializing) {
      return;
    }
    if (!this.isInitRequired()) {
      return;
    }
    this.resetStore();
    this._state = RailState.PENDING;
    this.isInitializing = true;
    const pageAmount = this.lastIndex ? Math.ceil(this.lastIndex / this.pageSize) : 1;
    await this.loadDataPaginated(pageAmount);
    this._isReady = true;
    this.isInitializing = false;
  }

  @computed get lastIndex() {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');

    return recordRepository.getLastIndexByRailId(this.id);
  }

  protected async getData(
    pageIndex: number,
    items: Program[],
  ): Promise<{ items: (Program | CardPlaceholder)[]; totalCount: number; newItems: Program[] }> {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');

    const isNewNpvr = featureEnabler.variation('NEW_NPVR', false);
    const newNpvrParams = {
      groupBy: this.groupBy,
      groupingOptions: KalturaGroupingOption.include,
    };
    const newRailData = await recordRepository.getSingleRecordsAssets({
      statusIn: KalturaRecordingStatus.recorded,
      kSql: this.aggregatedKSQL,
      pageIndex: pageIndex,
      pageSize: DEFAULT_PAGE_SIZE,
      ...(isNewNpvr ? { ...newNpvrParams } : {}),
    });

    const newItems = newRailData.items
      .map((item) => {
        const itemProgram = assetToType(item, {
          newEpisodeBadgeDays: this.newEpisodeBadgeDays,
          recordingExpirationBoundary: this.recordingExpirationBoundary,
        });
        if (itemProgram) {
          const entity = creatorEntityStore(itemProgram) as Program;
          // @ts-expect-error TODO Fix
          if (!entity.record && entity.data.recordData.recordingId) {
            recordRepository.addSingleRecordToRepository(entity.id, {
              // @ts-expect-error TODO Fix
              id: entity.data.recordData.recordingId,
              // @ts-expect-error TODO Fix
              type: entity.data.recordData.recordingType,
              // @ts-expect-error TODO Fix
              viewableUntilDate: entity.data.recordData.viewableUntilDate,
              expiredInDays: entity.recordingExpirationInDays,
              // @ts-expect-error TODO Fix
              totalRecordedEpisodes: entity.data.recordData.totalRecordedEpisodes,
            });
          }
          return entity;
        }
      })
      .slice(0, this.pageSize) as Program[];

    this.innerItems.push(...newItems);

    recordRepository.saveRailItems(
      this.innerItems.map((i) => i.id),
      this.id,
    );

    return { items: this.innerItems, totalCount: newRailData.totalCount, newItems: newItems };
  }

  protected async getAdditionData(programsInPage: Program[]): Promise<void> {
    await addBookmarkToRecords(programsInPage);
    await addMosaicProgramsToPrograms(programsInPage, true);
    return undefined;
  }

  @action
  protected async restoreAdditionData(pageIndex: number, items: Program[]): Promise<void> {
    return this.getAdditionData(getItemsInPage(items, pageIndex));
  }

  protected getRestoreDataKsql(): string {
    return "(and asset_type='recording')";
  }
}
